import React, {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {removePill, replacePill, setPill} from 'src/components/criteria-strip';
import {ACCOUNT_STRIP_LABELS} from 'src/components/criteria-strip/constants';
import {selectAccountManager, selectSearchCriteria, setAccountManager} from 'src/components/criteria-strip/services/criteria-strip-slice';
import {FormInput} from 'src/components/input';
import {QueryArea} from 'src/components/query-area';
import {MAX_NUMBER_VALUE, MAX_SELECTED_ACCOUNTS, SEARCH_RESULT_TABS} from 'src/constants';
import {useAccountStatuses} from 'src/hooks/dictionaries';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {EntityType} from 'src/modules/search-criteria/components/controls/entity-type';

import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {FormSection} from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';

import {validatePbRtsId} from '../../utils';
import {AccountTeamMemberSuggest} from '../account-manager-suggest';
import {CrmIntegrations, LicenseType} from '../controls';
import {SearchCriteriaTabs} from '../search-criteria-tabs';

import {accountSearchCriterion} from './account-search.module.styl';

import type {AccountManagerSearchResult} from 'src/openapi/admin/models/AccountManagerSearchResult';
import type {Pill} from 'src/typedef';

const INPUT_WIDTH = 400;

export const AccountSearch = () => {
    const dispatch = useDispatch();
    const criteria = useSelector(selectSearchCriteria);

    const {accountStatuses} = useAccountStatuses();

    const {searchCriteriaAccount} = useEventTracking();

    const selectedManager = useSelector(selectAccountManager);

    const onSetAccountIds = useCallback((accountIdsPills: Pill<string>[]) => {
        searchCriteriaAccount({parameterName: ACCOUNT_STRIP_LABELS.accountIds});
        if (accountIdsPills.length) {
            dispatch(setPill({propName: 'accountIds', pill: accountIdsPills}));
        }
    }, [dispatch, searchCriteriaAccount]);

    const onRemoveAccountIds = useCallback((accountId: string) => {
        searchCriteriaAccount({parameterName: ACCOUNT_STRIP_LABELS.accountIds});
        dispatch(removePill({propName: 'accountIds', forRemove: accountId}));
    }, [dispatch, searchCriteriaAccount]);

    const onSetUserIds = useCallback((userIdsPills: Pill<string>[]) => {
        searchCriteriaAccount({parameterName: ACCOUNT_STRIP_LABELS.userIds});
        if (userIdsPills.length) {
            dispatch(setPill({propName: 'userIds', pill: userIdsPills}));
        }
    }, [dispatch, searchCriteriaAccount]);

    const onRemoveUserIds = useCallback((userId: string) => {
        searchCriteriaAccount({parameterName: ACCOUNT_STRIP_LABELS.userIds});
        dispatch(removePill({propName: 'userIds', forRemove: userId}));
    }, [dispatch, searchCriteriaAccount]);

    const onSetAccountNames = useCallback((accountNames: Pill<string>[]) => {
        searchCriteriaAccount({parameterName: ACCOUNT_STRIP_LABELS.accountNames});
        dispatch(setPill({propName: 'accountNames', pill: accountNames}));
    }, [dispatch, searchCriteriaAccount]);

    const onRemoveAccountNames = useCallback((accountName: string) => {
        searchCriteriaAccount({parameterName: ACCOUNT_STRIP_LABELS.accountNames});
        dispatch(removePill({propName: 'accountNames', forRemove: accountName}));
    }, [dispatch, searchCriteriaAccount]);

    const onAccountStatusesChange = useCallback((statuses: string[]) => {
        searchCriteriaAccount({parameterName: ACCOUNT_STRIP_LABELS.accountStatuses});
        if (statuses.length) {
            dispatch(replacePill({propName: 'accountStatuses', pill: statuses.map(value => ({value}))}));
        } else {
            dispatch(removePill({propName: 'accountStatuses'}));
        }
    }, [dispatch, searchCriteriaAccount]);

    const onSetEntityType = useCallback((entityType: string) => {
        searchCriteriaAccount({parameterName: ACCOUNT_STRIP_LABELS.entityType});
        if (entityType) {
            dispatch(setPill({propName: 'entityType', pill: {value: entityType}}));
        } else {
            dispatch(removePill({propName: 'entityType'}));
        }
    }, [dispatch, searchCriteriaAccount]);

    const changeLicenseType = useCallback((licenseType: string, includeInactiveLicenses: boolean) => {
        searchCriteriaAccount({parameterName: ACCOUNT_STRIP_LABELS.licenseType});
        if (licenseType) {
            dispatch(replacePill({
                propName: 'licenseType',
                pill: [{value: licenseType}, ...(includeInactiveLicenses ? [{value: 'includeInactiveLicenses'}] : [])],
            }));
        } else {
            dispatch(removePill({propName: 'licenseType'}));
        }
    }, [dispatch, searchCriteriaAccount]);

    const onSetAccountManager = useCallback((manager: AccountManagerSearchResult | undefined) => {
        searchCriteriaAccount({parameterName: ACCOUNT_STRIP_LABELS.accountManagerId});
        if (manager) {
            dispatch(setAccountManager(manager));
            dispatch(setPill({propName: 'accountManagerId', pill: {value: manager.id}}));
        } else {
            dispatch(removePill({propName: 'accountManagerId'}));
        }
    }, [dispatch, searchCriteriaAccount]);

    const setPbRtsId = useCallback((rtsId: string) => {
        if ((criteria?.rtsId?.value ?? '') !== rtsId) {
            searchCriteriaAccount({parameterName: ACCOUNT_STRIP_LABELS.rtsId});
        }

        if (rtsId) {
            dispatch(setPill({
                propName: 'rtsId',
                pill: {value: rtsId, invalid: !validatePbRtsId(rtsId)},
            }));
        } else {
            dispatch(removePill({propName: 'rtsId'}));
        }
    }, [criteria, dispatch, searchCriteriaAccount]);

    const setPbSalesforceId = useCallback((sfdcId: string) => {
        if ((criteria?.sfdcId?.value ?? '') !== sfdcId) {
            searchCriteriaAccount({parameterName: ACCOUNT_STRIP_LABELS.sfdcId});
        }

        if (sfdcId) {
            dispatch(setPill({
                propName: 'sfdcId',
                pill: {value: sfdcId, invalid: !validatePbRtsId(sfdcId)},
            }));
        } else {
            dispatch(removePill({propName: 'sfdcId'}));
        }
    }, [criteria, dispatch, searchCriteriaAccount]);

    const changeCrmIntegrations = useCallback((crmIntegrations: string[], includeInactiveCrm: boolean) => {
        searchCriteriaAccount({parameterName: ACCOUNT_STRIP_LABELS.crmIntegrations});
        if (crmIntegrations.length) {
            dispatch(replacePill({
                propName: 'crmIntegrations',
                pill: [...crmIntegrations, ...includeInactiveCrm ? ['includeInactiveCrm'] : []].map(value => ({value})),
            }));
        } else {
            dispatch(removePill({propName: 'crmIntegrations'}));
        }
    }, [dispatch, searchCriteriaAccount]);

    if (!criteria) {
        return null;
    }

    return (
        <Group direction="v" gap="xl">
            <SearchCriteriaTabs activeTab={SEARCH_RESULT_TABS.ACCOUNTS}/>
            <FormSection borderBottom={true} headline="Account Criteria">
                <Group alignItems="start" gap="xxl">
                    <Group direction="v">
                        <div className={accountSearchCriterion}>
                            <Label>{ACCOUNT_STRIP_LABELS.accountNames}</Label>
                            <QueryArea
                                items={criteria?.accountNames || []}
                                onValidate={name => name.length <= 200}
                                placeholder="Enter account names"
                                onAdd={onSetAccountNames}
                                onRemove={onRemoveAccountNames}
                                maxSelected={MAX_SELECTED_ACCOUNTS}
                            />
                        </div>
                        <LicenseType width={INPUT_WIDTH} criteria={criteria} onChange={changeLicenseType}/>
                        <div className={accountSearchCriterion}>
                            <Label>{ACCOUNT_STRIP_LABELS.accountStatuses}</Label>
                            <ComboBox
                                value={criteria.accountStatuses?.map(({value}) => value) || []}
                                placeholder="Select account statuses"
                                onChange={onAccountStatusesChange}>
                                {accountStatuses.map(({code, description}) => (
                                    <ComboboxItem caption={description} value={code} key={code}/>
                                ))}
                            </ComboBox>
                        </div>
                        <div>
                            <AccountTeamMemberSuggest
                                width={400}
                                placeholder="Select team member"
                                label={ACCOUNT_STRIP_LABELS.accountManagerId}
                                selectedTeamMember={selectedManager || undefined}
                                onSelect={onSetAccountManager}
                            />
                        </div>
                        <div className={accountSearchCriterion}>
                            <Label>{ACCOUNT_STRIP_LABELS.userIds}</Label>
                            <QueryArea
                                placeholder="Enter user IDs"
                                items={criteria.userIds || []}
                                onValidate={(id: string) => !isNaN(+id) && +id > 0 && +id < MAX_NUMBER_VALUE}
                                onAdd={onSetUserIds}
                                onRemove={onRemoveUserIds}
                                maxSelected={MAX_SELECTED_ACCOUNTS}
                            />
                        </div>
                    </Group>
                    <Group direction="v">
                        <div className={accountSearchCriterion}>
                            <Label>{ACCOUNT_STRIP_LABELS.accountIds}</Label>
                            <QueryArea
                                placeholder="Enter account IDs"
                                items={criteria.accountIds || []}
                                onValidate={(id: string) => !isNaN(+id) && +id > 0 && +id < MAX_NUMBER_VALUE}
                                onAdd={onSetAccountIds}
                                onRemove={onRemoveAccountIds}
                                maxSelected={MAX_SELECTED_ACCOUNTS}
                            />
                        </div>
                        <EntityType onSelect={onSetEntityType} selectedEntityType={criteria?.entityType?.value}/>
                        <div className={accountSearchCriterion}>
                            <Label>Cross Reference</Label>
                            <Group justify="space-between">
                                <FormInput
                                    valid={criteria?.rtsId?.invalid ? !criteria?.rtsId?.invalid : undefined}
                                    width={190}
                                    maxLength={12}
                                    placeholder="PB RTS ID"
                                    value={criteria?.rtsId?.value || ''}
                                    onBlur={setPbRtsId}
                                />
                                <FormInput
                                    valid={criteria?.sfdcId?.invalid ? !criteria?.sfdcId?.invalid : undefined}
                                    width={190}
                                    maxLength={18}
                                    placeholder="PB Salesforce ID"
                                    value={criteria?.sfdcId?.value || ''}
                                    onBlur={setPbSalesforceId}
                                />
                            </Group>
                        </div>
                        <CrmIntegrations
                            width={INPUT_WIDTH}
                            criteria={criteria}
                            onChange={changeCrmIntegrations}
                        />
                    </Group>
                </Group>
            </FormSection>
        </Group>
    );
};
